import { Box, Divider, HStack, Image, Text, VStack } from '@chakra-ui/react';
import { useNavigate } from '@tanstack/react-router';
import CustomButton from '@/components/atoms/CustomButton';
import LoopStage from '@/modules/content/loop/parts/LoopStage';
import MediaSkeleton from '@/modules/content/loop/parts/MediaSkeleton';
import PortraitFrame from '@/modules/content/loop/parts/PortraitFrame';
import TextColumn from '@/modules/content/loop/parts/TextColumn';
import { getTitledSections, readDetails } from '@/modules/content/loop/model/contentDetails';
import { TLoopVariantProps } from '@/modules/content/loop/model/types';

const MAX_SECTION_CHIPS = 4;

// Article — a preview, never the full read. "Read Full Article" opens the article route,
// which renders the same ContentReader.
const ArticleVariant = ({ content, onNotInterested, onReport, rail, nav }: TLoopVariantProps) => {
  const navigate = useNavigate();
  const details = readDetails(content);
  const sections = getTitledSections(content);

  const excerpt = details.fullDescription || sections[0]?.text || '';
  const inlineImage = sections.find((section) => section.imageUrl)?.imageUrl;
  const companionImage = content.effectiveThumbnailUrl ?? content.thumbnailUrl;

  return (
    <LoopStage
      left={
        <TextColumn content={content} onNotInterested={onNotInterested} onReport={onReport}>
          <Divider borderColor="background.separatorBlue" />

          {excerpt && (
            <Text fontSize="13px" color="text.mediumBlue" lineHeight="19px" noOfLines={10}>
              {excerpt}
            </Text>
          )}

          {inlineImage && (
            <Image src={inlineImage} alt="" width="100%" borderRadius="12px" objectFit="cover" maxHeight="150px" />
          )}

          {sections.length > 0 && (
            <VStack align="stretch" gap="8px" paddingTop="4px">
              <Text fontSize="13px" fontWeight={700} color="text.darkBlue">
                Article Sections
              </Text>
              {sections.slice(0, MAX_SECTION_CHIPS).map((section, index) => (
                <HStack
                  key={section._id ?? index}
                  gap="10px"
                  align="center"
                  padding="8px"
                  borderRadius="10px"
                  backgroundColor="white"
                  boxShadow="0px 4px 12px rgba(14, 0, 98, 0.06)"
                >
                  <HStack
                    justify="center"
                    width="24px"
                    height="24px"
                    borderRadius="6px"
                    backgroundColor="primary.500"
                    color="white"
                    flexShrink={0}
                  >
                    <Text fontSize="12px" fontWeight={700}>
                      {index + 1}
                    </Text>
                  </HStack>
                  <Text fontSize="12px" fontWeight={600} color="text.darkBlue" noOfLines={2}>
                    {section.title}
                  </Text>
                </HStack>
              ))}
            </VStack>
          )}

          <Box paddingTop="4px">
            <CustomButton
              label="Read Full Article →"
              width="100%"
              backgroundColor="secondary.500"
              hoverBackgroundColor="secondary.600"
              labelColor="white"
              onClick={() => navigate({ to: '/article/$contentId', params: { contentId: content.id } })}
            />
          </Box>
        </TextColumn>
      }
      center={
        <PortraitFrame>
          {companionImage ? (
            <Image src={companionImage} alt="" width="100%" height="100%" objectFit="cover" />
          ) : (
            <MediaSkeleton />
          )}
        </PortraitFrame>
      }
      right={
        <>
          {rail}
          {nav}
        </>
      }
    />
  );
};

export default ArticleVariant;
